// Payment Models for TDT-Web (Public Portal)

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED'
}

export enum PaymentMethodType {
  ACH = 'ACH',
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD'
}

export enum CardType {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DISCOVER = 'DISCOVER'
}

export interface ReturnSummary {
  id: number;
  period: string;
  type: string;
  frequency: string;
  dueDate: string;
  status: string;
  totalAmountDue: number;
  balanceDue: number;
  amountPaid: number;
  propertyCount?: number;
  properties?: string[];
  propertyLines?: { address: string; grossRentalReceipts: number }[];

  // Calculation inputs (used by the Return view; optional until backend supplies them)
  taxRate?: number;                  // e.g. 0.05 for 5%
  totalTaxes?: number;
  taxableRentalReceipts?: number;
  lateFee?: number;
  lateInterest?: number;
  onlineCollectionAllowance?: number;
  origin?: string;                   // ReturnOrigin name, e.g. 'SUPPLEMENTAL'
  // Shared property returns: home account of the return (null/absent = the caller's own).
  owningAccountId?: number;
  owningAccountName?: string;
  shared?: boolean;
}

// Full return detail used by the Return view (GET /external/payments/returns/{id}/detail)
export interface ReturnPropertyRow {
  id: number;
  address: string;
  grossRentalReceipts: number;
  exemptions: number;
}

export interface ReturnDetail {
  id: number;
  period: string;
  type: string;
  frequency: string;
  dueDate: string;
  status: string;
  taxRate: number;
  lateFee: number;
  lateInterest: number;
  onlineCollectionAllowance: number;
  taxableRentalReceipts: number;
  totalTaxes: number;
  totalAmountDue: number;
  balanceDue: number;
  properties: ReturnPropertyRow[];
  origin?: string;          // ReturnOrigin name, e.g. 'SUPPLEMENTAL'
  sourceReturnId?: number;  // supplemental: the already-paid return for the same period
  owningAccountId?: number;
  owningAccountName?: string;
  shared?: boolean;
  editableReturnPropertyIds?: number[]; // lines the caller may edit (absent = all)
  version?: number;                     // optimistic lock, echoed back as expectedVersion
  reportedByRole?: string;              // OWNER | MANAGEMENT_COMPANY
  reportedByAccountName?: string;
}

// Request body for PUT /external/payments/returns/{id}
export interface ReturnUpdate {
  properties: { id: number; grossRentalReceipts: number; exemptions: number }[];
  expectedVersion?: number; // required by the server; mismatch → 409
}

// One scheduled installment within a plan
export interface Installment {
  sequenceNo: number;        // 1-based
  period: string;            // "May | 2025"
  dueDate: string;
  status: string;            // Paid | Unpaid | ...
  amount: number;
  returnId?: number;         // the tax return this installment settles, if applicable
}

// A return that belongs to a group/plan (shown in the "Return group" modal)
export interface GroupedReturn {
  returnId: number;
  period: string;            // "Nov | 2024"
  frequency: string;
  amount: number;
}

// Installment plan covering one or more grouped returns
export interface InstallmentPlan {
  planId: number;
  frequency: string;
  totalAmount: number;
  totalPaid: number;
  totalLeft: number;
  installmentCount: number;       // e.g. 4 -> "Installment x/4"
  currentSequenceNo?: number;     // which installment this return is (the x in x/4)
  installments: Installment[];
  groupedReturns: GroupedReturn[];
  nextDueAmount?: number;         // next amount owed in one payment (down payment or next installment)
  nextDueLabel?: string;          // e.g. "Down payment" or "Installment 1/6"
}

export interface PaymentMethod {
  id?: number;
  type: PaymentMethodType;
  amount: number;

  // ACH details
  accountHolderName?: string;
  bankName?: string;
  accountType?: 'CHECKING' | 'SAVINGS';
  routingNumber?: string;
  accountNumberLast4?: string;

  // Card details
  cardHolderName?: string;
  cardLastFour?: string;
  cardType?: CardType;
  cardExpMonth?: number;
  cardExpYear?: number;
  cvv?: string;
  billingZipCode?: string;

  // Fees
  processingFeePercentage?: number;
  processingFeeAmount?: number;
}

export interface PayerDetails {
  billingStreet?: string;
  billingAptSuite?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  billingCountry?: string;
  accountClass?: string;   // BUSINESS | PERSONAL
  email?: string;
  phone?: string;
  receiptMethod?: string;  // EMAIL | TEXT
  memo?: string;
}

export interface PaymentRequest {
  returnIds: number[];
  // Optional: a return saved as $0 is filed with no payment method.
  paymentMethod?: PaymentMethod;
  totalAmount: number;
  savePaymentMethod?: boolean;
  payerDetails?: PayerDetails;
  // When true, pay the plan's next amount due (down payment or next installment) as a partial
  // payment against a single plan return. The server derives the authoritative amount.
  payNextInstallment?: boolean;
}

export interface Payment {
  id: number;
  confirmationNumber: string;
  transactionId?: string;
  accountId: number;
  accountName: string;
  customerId: string;
  status: PaymentStatus;
  paymentMethod: PaymentMethod;
  returnIds: number[];
  returns: ReturnSummary[];
  totalAmountDue: number;
  processingFee: number;
  totalPaid: number;
  paymentDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentConfirmation {
  confirmationNumber: string;
  transactionId?: string;
  paymentDate: string;
  returns: ReturnSummary[];
  paymentMethod: PaymentMethod;
  subtotal: number;
  processingFee: number;
  totalPaid: number;
  accountName: string;
  customerId: string;
}

// ===== Hosted checkout (Catalis) =====

// Response for GET /external/payments/gateway-status
export interface GatewayStatusInfo {
  processor: string;
  hostedCheckout: boolean;
}

// Response for POST /external/payments/checkout/start
export interface CheckoutStartResponse {
  checkoutUrl: string;
  transactionId: string;
  ref: number;
}

export interface SavedPaymentMethod {
  id: number;
  type: PaymentMethodType;
  isDefault: boolean;

  // ACH
  accountHolderName?: string;
  bankName?: string;
  accountType?: string;
  accountNumberLast4?: string;

  // Card
  cardHolderName?: string;
  cardLastFour?: string;
  cardType?: CardType;
  cardExpMonth?: number;
  cardExpYear?: number;

  createdAt: string;
  updatedAt: string;
}

export interface PaymentHistory {
  payments: Payment[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PaymentDashboard {
  outstandingReturns: ReturnSummary[];
  recentPayments: Payment[];
  totalOutstanding: number;
  savedPaymentMethods: SavedPaymentMethod[];
}

// ===== Scheduled payments (portal "Schedule for later") =====

export interface CreateScheduledPayment {
  returnIds: number[];
  savedPaymentMethodId: number;
  scheduledDate: string; // ISO date, must be in the future
}

export interface ScheduledPayment {
  id: number;
  scheduledDate: string;
  amount: number;
  status: 'PENDING' | 'PROCESSED' | 'FAILED' | 'CANCELLED';
  methodLabel: string;
  returns: { id: number; period: string; type: string; balanceDue: number }[];
  paymentId?: number;
  failureMessage?: string;
}
